// Central dayjs instance — ALWAYS import dayjs from here, never from 'dayjs'.
//
//   import dayjs from 'shared/dayjs';   // correct
//   import dayjs from 'dayjs';          // WRONG - plugins may not be registered
//                                       // (and rejected by no-restricted-imports)
//
// Importing through this module makes plugin registration a data dependency: ES
// module semantics guarantee this file's body runs before any importer's body,
// under any code-splitting boundary. A side-effect-only import in index.tsx would
// NOT give that guarantee — it would recreate the load-order fragility moment-timezone
// had, whose `.tz` patch existed only once some module happened to import it.
//
// The plugin set below is the minimum this codebase actually needs. It was chosen
// by auditing every moment API in use and verified to produce byte-identical
// output to moment for every format string and timezone pattern in the app.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';

// Locales must match the app's supported cultures (see App.tsx / i18n config).
// 'en' is dayjs's built-in default and needs no import.
import 'dayjs/locale/de';
import 'dayjs/locale/sr';
import 'dayjs/locale/pl';
import 'dayjs/locale/tr';

dayjs.extend(utc);               // .utc(), .local()
dayjs.extend(timezone);          // .tz(...) — used in 224 places
dayjs.extend(customParseFormat); // dayjs(value, 'DD.MM.YYYY') — SILENTLY ignores
                                 // the format argument if this is missing
dayjs.extend(isSameOrAfter);     // .isSameOrAfter() — yup schemas, employee-interfaces
dayjs.extend(isSameOrBefore);    // .isSameOrBefore()
dayjs.extend(isoWeek);           // .isoWeekday() — isoWeekdayName below

// Correctness fix for the dayjs timezone plugin - KEEP after the migration.
//
// dayjs's .tz(zone) works out the zone's offset by printing the instant as that zone's wall-clock time
// and parsing the text back with new Date() - that is, in the MACHINE's own zone. Wherever that text
// names a time the machine's zone repeats or skips, the offset comes out an hour wrong:
//   - the second 02:00-02:59 on the night summer time ends: 2026-10-25T01:30:00Z in Europe/Belgrade
//     became "02:30 at +00:00", an instant an hour later (moment-timezone: 02:30 at +01:00);
//   - .tz('UTC') inside the machine's own DST change: 2024-03-31T02:30:00Z showed as 03:30.
// So the offset is read from Intl instead - the zone's wall-clock fields for this exact instant,
// compared with the instant itself - which depends on nothing but the instant and the zone. Covered by
// shared/dayjs.dst.test.ts.
//
// The same fields also build the wall-clock date, in place of the plugin's toLocaleString text, which
// creates a new formatter on every call: .tz() runs for nearly every date the app shows, and this
// makes it several times faster. The date built is the one the text gave.
//
// One limit stays, as before: a wall-clock time that does not exist in the machine's own zone (the
// hour skipped when its summer time starts) cannot be shown by a dayjs object at all. It only matters
// when the user's time zone setting differs from the browser's.
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

type ZoneWallClock = { year: number; month: number; day: number; hour: number; minute: number; second: number; offset: number };

// The zone's wall-clock fields at an instant, and its UTC offset there in minutes.
const readZoneWallClock = (timestamp: number, zone: string): ZoneWallClock => {
    let formatter = zoneFormatters.get(zone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            hourCycle: 'h23',
            year: 'numeric', month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: 'numeric', second: 'numeric',
        });
        zoneFormatters.set(zone, formatter);
    }
    const field: Record<string, number> = {};
    for (const part of formatter.formatToParts(timestamp)) field[part.type] = Number(part.value);
    const hour = field.hour % 24;
    // The year is set separately: Date.UTC maps years 0-99 onto 1900-1999.
    const asUtc = new Date(Date.UTC(2000, field.month - 1, field.day, hour, field.minute, field.second));
    asUtc.setUTCFullYear(field.year);
    const offset = Math.round((asUtc.getTime() - Math.floor(timestamp / 1000) * 1000) / 60000);
    return { year: field.year, month: field.month, day: field.day, hour, minute: field.minute, second: field.second, offset };
};

dayjs.extend((_option, DayjsClass) => {
    const proto = DayjsClass.prototype as unknown as {
        tz: (zone?: string, keepLocalTime?: boolean) => any;
        isValid: () => boolean;
        valueOf: () => number;
        millisecond: () => number;
        toDate: () => Date;
        utcOffset: (offset?: number, keepLocalTime?: boolean) => any;
        $L: string;
    };
    const originalTz = proto.tz;
    proto.tz = function (zone?: string, keepLocalTime?: boolean) {
        // No zone means the plugin's default zone, which it keeps to itself.
        if (!zone || !this.isValid()) return originalTz.call(this, zone, keepLocalTime);
        const wall = readZoneWallClock(this.valueOf(), zone);
        const offset = wall.offset;
        let converted;
        if (offset === 0) {
            // Kept in UTC mode, so it is shown through the UTC getters and no machine-zone hour can
            // shift it. This also covers .tz('UTC').
            converted = this.utcOffset(0, keepLocalTime);
        } else {
            // Built the way the plugin builds it - the zone's wall-clock time as a local date, then
            // labelled with the offset - so the time shown is unchanged; only the offset, and with it
            // the instant, is now right. Years before 1000 (in practice only .NET's 0001-01-01) still
            // go through the plugin's text: the browser reads its year with its own two-digit rules.
            const oldOffset = this.utcOffset();
            const localWallClock = wall.year >= 1000
                ? new Date(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
                : this.toDate().toLocaleString('en-US', { timeZone: zone });
            const fromWallClock = dayjs(localWallClock, { locale: this.$L } as any).millisecond(this.millisecond()).utcOffset(offset, true);
            // Inside the machine's skipped hour the local date lands an hour off. Converting the instant
            // there keeps the instant right; the hour shown is the limit described above.
            converted = fromWallClock.valueOf() === this.valueOf() ? fromWallClock : this.utcOffset(offset);
            if (keepLocalTime) converted = converted.add(oldOffset - converted.utcOffset(), 'minute');
        }
        converted.$x.$timezone = zone;
        return converted;
    };
});

// Backslash-escape compatibility — KEEP after the migration.
//
// The backend's US date format is the 12-character string  MM\/DD\/YYYY  — it
// really contains backslashes (see ZeitonBackend TenantSettingsConstants.cs, where
// the C# literal is "MM\\/DD\\/YYYY"). moment strips backslashes from the output;
// dayjs has no backslash-escape support and emits them literally:
//     moment -> "03/09/2026"        dayjs (unpatched) -> "03\/09\/2026"
// Every user whose date format is the US option would see the backslashes.
//
// Stripping backslashes reproduces moment's output for every format string this
// app can actually receive (verified across all 6 backend formats, the app's
// hardcoded display formats, and the ISO/bracket-escape serialization formats).
// Note the app escapes literals with dayjs/moment bracket syntax — `[Z]` — not
// backslashes, so nothing here relies on backslash-escaping a format token.
const BACKSLASH = String.fromCharCode(92);

dayjs.extend((_option, DayjsClass) => {
    const proto = DayjsClass.prototype as unknown as { format: (formatStr?: string) => string };
    const originalFormat = proto.format;
    proto.format = function (formatStr?: string): string {
        if (typeof formatStr === 'string' && formatStr.indexOf(BACKSLASH) !== -1) {
            return originalFormat.call(this, formatStr.split(BACKSLASH).join(''));
        }
        return originalFormat.call(this, formatStr);
    };
});

// Migration parity shim — REMOVE once the moment migration is finished (Phase 2).
// moment renders invalid/null input as "Invalid date"; dayjs uses "Invalid Date"
// (capital D). ~128 inline format calls can receive null, so without this the
// swap would be a visible text change. This keeps Phase 1 provably pixel-identical.
// Phase 2 routes those calls through helpers that return "-" instead, at which
// point this shim goes away.
dayjs.extend((_option, DayjsClass) => {
    const proto = DayjsClass.prototype as unknown as {
        format: (formatStr?: string) => string;
        isValid: () => boolean;
    };
    const originalFormat = proto.format;
    proto.format = function (formatStr?: string): string {
        return this.isValid() ? originalFormat.call(this, formatStr) : 'Invalid date';
    };
});

// moment(x, format) applies the format only to a string and takes a Date as it is; dayjs's
// customParseFormat applies it to anything and turns a Date into Invalid Date. Every
// moment(x, format) the migration converts goes through this, so a call site does not have to
// prove that x can never be a Date (accessorFns build them, and so do records created in the
// browser). Same result as moment for strings, null, undefined and Dates.
export const parseUnlessDate = (value: dayjs.ConfigType, format: string) =>
    value instanceof Date ? dayjs(value) : dayjs(value, format);

// English name of an ISO weekday (1 = Monday … 7 = Sunday). The app uses these names as i18n keys
// (t('Monday')), and dayjs is localized globally (App.tsx), so without the pinned locale a German
// user would get "Montag", miss the lookup, and see the raw key.
export const isoWeekdayName = (isoWeekday: number) =>
    dayjs().locale('en').isoWeekday(isoWeekday).format('dddd');

// The backend sends an unset (non-nullable) DateTime as .NET's default(DateTime): 0001-01-01. moment
// showed it as 01.01.0001, but dayjs builds dates through new Date(y, ...), which maps years 0-99 onto
// 1900-1999, so the same value becomes 01.01.1901 (or 2001 after .tz()) - a believable, wrong date.
// The shared formatters treat it as no date. The year is read from a native Date, which parses an ISO
// string without that mapping.
export const isDotNetDefaultDate = (value: string | Date | null | undefined): boolean => {
    if (!value) return false;
    const date = value instanceof Date ? value : new Date(value);
    return !isNaN(date.getTime()) && date.getUTCFullYear() <= 1;
};

// A date-time picked in local time, as the UTC instant the API stores ("...Z"). Formatting the local
// time and appending a literal Z instead labels 14:00 local as 14:00 UTC: it comes back as 15:00
// (16:00 in summer), and a controlled picker adds the offset again on every change. Use it only
// where the value is read back as an instant - not for the fields stored as wall-clock time.
export const toUtcString = (value: dayjs.Dayjs): string => value.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

export type { Dayjs } from 'dayjs';
export default dayjs;
